'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Users, Bell, Shield, HelpCircle, ChevronRight, Info } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { getUnreadAlertCount, getUnreadMessageCount } from '@/lib/demoData';

const SETTINGS_ITEMS = [
  {
    id: 'contacts',
    icon: Users,
    label: 'Contacts et autorisations',
    description: 'Gérer les proches autorisés à contacter André',
    href: '/contacts',
    highlight: true,
    ariaLabel: 'Ouvrir les contacts et autorisations',
  },
  {
    id: 'notifications',
    icon: Bell,
    label: 'Notifications',
    description: 'Alertes, rappels et préférences',
    href: '/settings/notifications',
    highlight: false,
    ariaLabel: 'Ouvrir les paramètres de notifications',
  },
  {
    id: 'privacy',
    icon: Shield,
    label: 'Confidentialité',
    description: 'Données et sécurité du compte',
    href: '/settings/privacy',
    highlight: false,
    ariaLabel: 'Ouvrir les paramètres de confidentialité',
  },
  {
    id: 'help',
    icon: HelpCircle,
    label: 'Aide et support',
    description: 'Questions fréquentes et assistance',
    href: '/settings/help',
    highlight: false,
    ariaLabel: "Ouvrir l\'aide et le support",
  },
  {
    id: 'about',
    icon: Info,
    label: 'À propos de LUCAT CONNECT',
    description: 'Version, mentions légales',
    href: '/settings/about',
    highlight: false,
    ariaLabel: 'Afficher les informations sur LUCAT CONNECT',
  },
];

export default function SettingsClient() {
  const router = useRouter();
  const unreadAlerts = getUnreadAlertCount();
  const unreadMessages = getUnreadMessageCount();

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        <button
          onClick={() => router?.back()}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour à la page précédente"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
        <h1 className="text-xl font-bold text-white">Paramètres</h1>
        <p className="text-sm text-white/70 mt-0.5">Gérer votre compte et vos préférences</p>
      </div>
      {/* Settings list */}
      <div className="px-4 py-5 space-y-2">
        {SETTINGS_ITEMS?.map(({ id, icon: ItemIcon, label, description, href, highlight, ariaLabel }) => (
          <Link
            key={id}
            href={href}
            className={`flex items-center gap-4 bg-white rounded-2xl shadow-card p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer ${
              highlight ? 'border-l-4 border-lucat-green' : ''
            }`}
            aria-label={ariaLabel}
            style={{ minHeight: 64 }}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-lucat-green-light' : 'bg-gray-100'}`}>
              {React.createElement(ItemIcon, { size: 20, className: highlight ? 'text-lucat-green' : 'text-gray-500' })}
            </div>
            <div className="flex-1 min-w-0 pr-2">
              <p className={`font-semibold text-sm ${highlight ? 'text-lucat-navy' : 'text-gray-800'}`}>{label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            </div>
            <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </AppLayout>
  );
}
